<template>
  <div class="container-fluid page-container">
    <div v-if="isChild">
      <router-view />
    </div>
    <div v-else>
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <b-row v-if="totalReviews === 0" class="vh-70">
        <b-col class="col-sm-7 m-auto">
          <b-card :body-class="['shadow', 'border', 'rounded', 'py-5', 'text-center']">
            <b-card-text>
              <h2 class="mb-0 h3 px-3">
                {{ $t('Sorry, you don\'t have any reviews currently.') }}
              </h2>
              <hr class="w-50">
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
      <div v-else class="row">
        <div class="col-12 order-1 order-xl-0 col-xl-9 feedback__container">
          <feedback-list :sort="sortBy" :filter="filterBy" />
        </div>
        <div v-if="totalReviews != null" class="col order-0 order-xl-1 col-xl-3">
          <div class="row mb-3">
            <div class="col-md-6 col-xl-12">
              <b-card no-body class="bg-white h-100 border rounded shadow w-100 p-2 mb-3 p-md-4 d-flex text-center justify-content-center align-items-center">
                <h2 class="h4">
                  {{ $t('Published Reviews Rating') }}
                </h2>
                <div v-if="averageRating" class="my-md-3">
                  <div>{{ averageRating.toFixed(1) }}/5</div>
                  <div class="h1">
                    <b-form-rating
                      v-model="averageRating"
                      class="b__rating-feedback"
                      no-border
                      precision="2"
                      readonly
                    />
                  </div>
                </div>
                <div class="font-size-14">
                  {{ $tc('Published Reviews:', publishedReviews) }}
                </div>
                <div class="font-size-14">
                  {{ $tc('Total Reviews:', totalReviews) }}
                </div>
              </b-card>
            </div>
            <div class="col-md-6 col-xl-12 mt-3 mt-md-0 mt-xl-3">
              <b-card
                no-body
                class="bg-white border rounded shadow w-100 p-2 p-md-4 text-center"
              >
                <h2 class="h4 pb-2">
                  {{ $t('How your Reviews are distributed') }}
                </h2>
                <a
                  v-for="rating in distributionIndexes"
                  :key="rating"
                  v-b-tooltip.hover
                  :class="['position-relative',
                           {active: (filterBy.overallRating === rating),
                            'cursor-not-allowed': ratingDistribution[rating - 1] === 0,
                            'stretched-link': ratingDistribution[rating - 1] === 0 }]"
                  :title="ratingDistribution[rating - 1] === 0 ? '0 Reviews': ''"
                  @click="ratingDistribution[rating - 1] ? onRatingClicked(rating) : null"
                >
                  <b-progress
                    class="distribution__progress"
                    :max="totalReviews"
                  >
                    <span class="distribution__rating">{{ $tc('pluralsKey.star', rating) }}</span>
                    <b-progress-bar :value="ratingDistribution[rating-1]" />
                    <span class="distribution__count">
                      {{ $tc('pluralsKey.review', ratingDistribution[rating - 1]) }}
                    </span>
                  </b-progress>
                </a>
              </b-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FeedbackList from "@/components/list/FeedbackList";
import PageHeading from "@/components/control/PageHeading";

export default {
  name: "Reviews",
  title: 'Reviews',
  components: {PageHeading, FeedbackList},
  data() {
    return {
      sortBy: null,
      filterBy: {},
      averageRating: null,
      publishedReviews: null,
      totalReviews: null,
      companyFeedbackScore: null,
      ratingDistribution: [0, 0, 0, 0, 0],
      sortFields: [{key:'createdDate', label:'Created Date', canSort:true}],
    }
  },
  computed: {
    isChild: function() {
      return this.$router.currentRoute.name !== 'reviews'
    },
    distributionIndexes: () => [5, 4, 3, 2, 1],
    pageTitle(){
      return this.$t('Reviews');
    }
  },
  methods: {
    onRatingClicked(rating) {
      this.filterBy = (this.filterBy.overallRating !== rating) ? {overallRating: rating} : {};
    },
    setAverageRating(avgRating) {
      this.averageRating = parseFloat(avgRating) / 2
    }
  },
  created() {
    this.busy = true;
    this.$api.feedback.companyStats().then((result) => {
      const {average, ratings} = result.data.data;
      this.setAverageRating(parseFloat(average.rating ?? '0.0'));
      this.totalReviews = parseInt(average.total ?? '0');
      this.publishedReviews = parseInt(average.countPublished ?? '0');
      this.companyFeedbackScore = ratings.reverse();
      for (const rating of ratings) {
        this.ratingDistribution[parseInt(rating.rating) - 1] = parseInt(rating.count);
      }
    })
      .finally(() => this.busy = false);
  }
}
</script>

<style scoped lang="scss">
  ::v-deep {
    .distribution {
      &__progress {
        position: relative;
        margin-bottom: 10px;
        height: 2rem;

        &:hover {
          cursor: pointer;
        }

        &.progress {
          height: 2rem;
          line-height: 1.4;
        }
      }

      &__count {
        position: absolute;
        right: 10px;
        margin: 7px 0;
      }

      &__rating {
        position: absolute;
        left: 10px;
        margin: 7px 0;
      }
    }

    .active > {
      .distribution__progress {
        border: 2px solid #b8c7cf;
      }
    }

    .disabled > {
      .distribution__progress {
        cursor: default;
      }
    }

    .b__rating-feedback {
      font-size: 1.8rem;
      color: $theme-rating-color;

      .b-rating-star {
        padding: 0 .1rem;
      }
    }
  }

  .progress {
    border-radius: 3px;
    border: 1px solid $body-bg;
    font-weight: 500;
    line-height: 1.6;

    .progress-bar {
      border-right: 0;
      background: #fef3cc;
    }
  }
</style>
